/**
 * @description Asynchronously loads videos with responsive support and fallback play button
 */
const lazyLoadVideos = () => {
  // Get all videos with the lazy loading class
  const lazyVideos = [...document.querySelectorAll('video.js-video-loader')];
  if (lazyVideos.length < 1) return;

  // First-row card videos wait for LCP to settle. Other videos remain viewport-controlled.
  const lcpDeferredVideos = new Set(
    lazyVideos.filter(video => video.hasAttribute('data-lazy-after-lcp'))
  );
  let lcpGateOpen = lcpDeferredVideos.size === 0;
  let resolveLcpGate;
  const lcpGatePromise = lcpDeferredVideos.size > 0
    ? new Promise(resolve => {
        resolveLcpGate = resolve;
      })
    : Promise.resolve();
  let lazyVideoObserver = null;

  // Screen size detection
  const getScreenType = () => ({
    isMobile: window.matchMedia('(max-width: 767px)').matches,
    isTablet: window.matchMedia('(min-width: 768px) and (max-width: 991px)').matches,
    isDesktop: window.matchMedia('(min-width: 992px)').matches
  });

  /**
   * Creates and adds a play button overlay to the video
   * @param {HTMLVideoElement} video - The video element
   */
  function addPlayButton(video) {
    // Prevent duplicate play buttons
    const existingBtn = video.closest('.shopify-section').querySelector('#PLAY_VIDEO_BTN');
    if (existingBtn) return;

    // Check if video is actually playing
    if (video.currentTime > 0 && !video.paused && !video.ended) {
      return;
    }

    let btn = document.createElement("button");
    btn.innerHTML = "&#9658;";
    btn.className = " " + video.parentElement.classList;
    btn.id = "PLAY_VIDEO_BTN";
    btn.setAttribute('aria-label', 'Play video');
    btn.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 9999;
      width: fit-content;
      font-size: 3rem;
      color: #fff;
      transform: translate(-50%, -50%);
      background: transparent;
      border: none;
      cursor: pointer;
    `;
    
    btn.addEventListener('click', () => {
      video.load();
      video.play().catch(() => {
        video.muted = true;
        video.play();
      });
      const playBtn = document.querySelector('#PLAY_VIDEO_BTN');
      if (playBtn) playBtn.remove();
    });
    
    video.closest(".shopify-section").append(btn);
  }

  /**
   * Checks if the video should swap the source for the specific screen
   * @param {HTMLVideoElement} video
   * @returns 
   */
  function checkResponsive(video) {
    const showOn = video.parentElement.dataset.responsiveShow?.split(',');
    const screenType = getScreenType();

    if (!showOn) return setSource(video);

    const conditions = {
      desktop: screenType.isDesktop,
      tablet: screenType.isTablet,
      mobile: screenType.isMobile,
    };

    if (showOn.some((device) => conditions[device.trim()])) {
      setSource(video);
    }
  }
  
  /**
   * Sets video source based on responsive conditions
   * @param {HTMLVideoElement} video - The video element
   * @returns {boolean} - Whether the source was set
   */
  function setSource(video) {
    let children = Array.from(video.children);
    children.forEach(child => {
      if (child.tagName === 'SOURCE' && child.dataset.src && child.src !== child.dataset.src) {
        child.setAttribute('src', child.dataset.src);
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
      }
    });
  }
  /**
   * Handles video loading and checks playability
   * @param {HTMLVideoElement} video - The video element
   */
  const loadVideo = (video) => {
    if (video._lazyVideoLoaded) return true;
    if (video.hasAttribute('data-lazy-after-lcp') && !lcpGateOpen) return false;

    video._lazyVideoLoaded = true;
    checkResponsive(video);

    const markLoaded = () => {
      video.classList.add('video-loaded');
      const playBtn = document.querySelector('#PLAY_VIDEO_BTN');
      if (playBtn) playBtn.remove();
    };

    const tryPlay = (allowButton) => {
      let promise;
      try {
        promise = video.play();
      } catch (e) {
        if (allowButton) addPlayButton(video);
        return;
      }
      if (promise && typeof promise.then === 'function') {
        promise.then(markLoaded).catch(() => {
          video.muted = true;
          let retry;
          try {
            retry = video.play();
          } catch (e) {
            if (allowButton) addPlayButton(video);
            return;
          }
          if (retry && typeof retry.then === 'function') {
            retry.then(markLoaded).catch(() => {
              if (allowButton) addPlayButton(video);
            });
          } else if (allowButton) {
            addPlayButton(video);
          }
        });
      } else if (allowButton) {
        setTimeout(() => {
          if (video.paused) addPlayButton(video);
        }, 1500);
      }
    };

    // Video already buffered: play right away
    if (video.readyState >= 3) {
      tryPlay(true);
      return true;
    }

    // Autoplay as soon as the media is playable
    let settled = false;
    const onReady = () => {
      if (settled) return;
      settled = true;
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('canplaythrough', onReady);
      tryPlay(true);
    };

    video.addEventListener('canplay', onReady);
    video.addEventListener('canplaythrough', onReady);

    // Safety net: if no media data arrives, expose a manual play button
    // so the user is never stuck on a static poster.
    setTimeout(() => {
      if (settled) return;
      if (video.readyState >= 2) onReady();
      else addPlayButton(video);
    }, 8000);

    video.addEventListener('error', () => {
      if (settled) return;
      addPlayButton(video);
    }, { once: true });

    video.load();
    return true;
  };

  const isInViewport = (video) => {
    if (video.offsetParent === null) return false;
    const rect = video.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  };

  /**
   * Releases first-row videos only after LCP and window load settle.
   * A hard timeout keeps autoplay reliable in browsers without LCP entries.
   */
  const armLcpGate = () => {
    if (lcpDeferredVideos.size === 0) return;

    let lcpSeen = false;
    let lcpObserverSupported = false;
    let loadComplete = document.readyState === 'complete';
    let releaseTimer;
    let released = false;
    let lcpObserver;

    const release = () => {
      if (released) return;
      released = true;
      if (releaseTimer) clearTimeout(releaseTimer);
      if (lcpObserver) lcpObserver.disconnect();
      resolveLcpGate();
    };

    const scheduleRelease = () => {
      if ((!lcpObserverSupported || lcpSeen) && loadComplete) {
        if (releaseTimer) clearTimeout(releaseTimer);
        releaseTimer = setTimeout(release, 500);
      }
    };

    if ('PerformanceObserver' in window) {
      try {
        lcpObserverSupported = true;
        lcpObserver = new PerformanceObserver(list => {
          if (list.getEntries().length === 0) return;
          lcpSeen = true;
          scheduleRelease();
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (error) {
        lcpObserverSupported = false;
      }
    }

    if (loadComplete) {
      scheduleRelease();
    } else {
      window.addEventListener('load', () => {
        loadComplete = true;
        scheduleRelease();
      }, { once: true });
    }

    setTimeout(release, 4000);
  };

  armLcpGate();

  lcpGatePromise.then(() => {
    lcpGateOpen = true;
    lcpDeferredVideos.forEach(video => {
      if (isInViewport(video) && loadVideo(video) && lazyVideoObserver) {
        lazyVideoObserver.unobserve(video);
      }
    });
  });

  // Check for videos already in viewport
  const checkInitialVideos = () => {
    lazyVideos.forEach(video => {
      if (isInViewport(video)) {
        loadVideo(video);
      }
    });
  };

  // Set up intersection observer if supported
  if ('IntersectionObserver' in window) {
    lazyVideoObserver = new IntersectionObserver(
      debounce(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && loadVideo(entry.target)) {
            lazyVideoObserver.unobserve(entry.target);
          }
        });
      }, 100)
    );

    lazyVideos.forEach(video => {
      video._lazyVideosObserved = true;
      lazyVideoObserver.observe(video);
    });
    checkInitialVideos();

    // Pick up videos injected after load (facets pagination, quick add, etc.)
    const processNode = (node) => {
      if (!(node instanceof HTMLElement)) return;
      const videos = node.matches && node.matches('video.js-video-loader')
        ? [node]
        : [...node.querySelectorAll('video.js-video-loader')];
      videos.forEach(video => {
        if (video.hasAttribute('fetchpriority') || video._lazyVideosObserved) return;
        if (video.hasAttribute('data-lazy-after-lcp')) lcpDeferredVideos.add(video);
        video._lazyVideosObserved = true;
        lazyVideoObserver.observe(video);
      });
    };
    new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(processNode);
      });
    }).observe(document.body, { childList: true, subtree: true });
  } else {
    // Fallback for browsers without IntersectionObserver
    checkInitialVideos();
    
    // Simple scroll-based loading
    window.addEventListener('scroll', debounce(() => {
      lazyVideos.forEach(video => {
        if (isInViewport(video)) {
          loadVideo(video);
        }
      });
    }, 100));
  }
};

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', lazyLoadVideos);